<template>
  <div class="login">
    <el-form ref="loginForm" :model="loginForm" :rules="loginRules" class="login-form">
      <h3 class="title">宝就业平台管理系统</h3>
      <el-form-item prop="account">
        <el-input v-model="loginForm.account" type="text" size="large" placeholder="帐号">
          <svg-icon slot="prefix" icon-class="user"></svg-icon>
        </el-input>
      </el-form-item>
      <el-form-item prop="password">
        <el-input
          v-model="loginForm.password"
          type="password"
          size="large"
          placeholder="密码"
          show-password
          @keyup.enter.native="handleLogin"
        >
          <svg-icon slot="prefix" icon-class="password"></svg-icon>
        </el-input>
      </el-form-item>
      <el-form-item>
        <SliderVerify
          ref="sliderRef"
          width="100%"
          :account="loginForm.account"
          :password="loginForm.password"
          @success="handleSliderSuccess"
          @error="handleSliderError"
        />
      </el-form-item>

      <template v-if="sliderVerified">
        <el-form-item prop="phone">
          <div class="login-field">
            <img class="login-field__icon" :src="phoneIcon" alt="">
            <input
              v-model="loginForm.phone"
              class="login-field__input"
              type="text"
              maxlength="11"
              placeholder="请输入手机号"
            />
          </div>
        </el-form-item>
        <el-form-item prop="code">
          <div class="login-sms-row">
            <div class="login-field login-field--flex">
              <img class="login-field__icon" :src="authIcon" alt="">
              <input
                v-model="loginForm.code"
                class="login-field__input"
                type="text"
                maxlength="6"
                placeholder="点击右侧按钮获取验证码"
                @keyup.enter.native="handleLogin"
              />
            </div>
            <button
              type="button"
              class="login-sms-btn"
              :disabled="smsSending || smsCountdown > 0"
              @click="handleSendSmsCode"
            >
              {{ smsCountdown > 0 ? `${smsCountdown}s后重发` : '发送验证码' }}
            </button>
          </div>
        </el-form-item>
        <el-form-item>
          <el-button :loading="loading" type="primary" @click.prevent="handleLogin" style="width: 100%;">
            <span v-if="!loading">登 录</span>
            <span v-else>登录中...</span>
          </el-button>
        </el-form-item>
      </template>
    </el-form>
    <div class="el-login-footer">
      <span></span>
    </div>
  </div>
</template>

<script>
import { encrypt } from '@/utils/jsencrypt'
import { getSmsCode } from '@/api/login'
import SliderVerify from './components/slider.vue'

export default {
  name: 'Login',
  components: {
    SliderVerify
  },
  data() {
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error('手机号不能为空'))
      } else {
        const reg = /^1[3-9]\d{9}$/
        if (!reg.test(value)) {
          callback(new Error('手机号格式不正确'))
        } else {
          callback()
        }
      }
    }
    return {
      phoneIcon: require('@/assets/images/login/phone.png'),
      authIcon: require('@/assets/images/login/auth.png'),
      loading: false,
      sliderVerified: false,
      smsSending: false,
      smsCountdown: 0,
      smsTimer: null,
      loginForm: {
        account: '',
        password: '',
        phone: '',
        code: ''
      },
      loginRules: {
        phone: [
          { validator: validatePhone, trigger: 'blur' }
        ],
        account: [
          { required: true, message: '帐号不能为空', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]+$/, message: '请输入正确格式的账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '密码不能为空', trigger: 'blur' }
        ],
        code: [{ required: true, trigger: 'blur', message: '验证码不能为空' }]
      }
    }
  },
  beforeDestroy() {
    this.clearSmsTimer()
  },
  methods: {
    handleSliderSuccess(data) {
      this.sliderVerified = true
      this.loginForm.phone = data?.phone || ''
    },
    handleSliderError() {
      this.resetVerifyState()
    },
    resetVerifyState() {
      this.sliderVerified = false
      this.loginForm.phone = ''
      this.loginForm.code = ''
      this.clearSmsTimer()
      this.smsCountdown = 0
    },
    clearSmsTimer() {
      if (this.smsTimer) {
        clearInterval(this.smsTimer)
        this.smsTimer = null
      }
    },
    startSmsCountdown(seconds = 60) {
      this.clearSmsTimer()
      this.smsCountdown = seconds
      this.smsTimer = setInterval(() => {
        if (this.smsCountdown <= 1) {
          this.clearSmsTimer()
          this.smsCountdown = 0
        } else {
          this.smsCountdown -= 1
        }
      }, 1000)
    },
    handleSendSmsCode() {
      this.$refs.loginForm.validateField('phone', async (errorMessage) => {
        if (errorMessage) return
        this.smsSending = true
        try {
          const res = await getSmsCode({
            username: this.loginForm.account.trim(),
            password: encrypt(this.loginForm.password),
            phone: this.loginForm.phone,
            deviceType: 'pc_web'
          })
          if (res && res.code === 200) {
            this.$message.success(res.msg || '验证码已发送')
            this.startSmsCountdown()
          } else {
            this.$message.error(res?.msg || '验证码发送失败')
          }
        } catch (error) {
          this.$message.error('验证码发送失败')
        } finally {
          this.smsSending = false
        }
      })
    },
    handleLogin() {
      if (!this.sliderVerified) {
        this.$message.warning('请先完成滑块验证')
        return
      }
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          this.$store.dispatch('LoginByAccount', {
            account: this.loginForm.account,
            password: encrypt(this.loginForm.password),
            phone: this.loginForm.phone,
            code: this.loginForm.code
          }).then(() => {
            this.$router.push({ path: '/jump' }).catch(() => {})
          }).catch(() => {
            this.loading = false
            this.loginForm.password = ''
            this.loginForm.code = ''
            this.resetVerifyState()
            this.$refs.sliderRef && this.$refs.sliderRef.reset()
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.login {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  background-image: url('../../assets/login/loginBg.png');
  background-size: cover;
  padding-right: 300px;
}

.title {
  margin: 0 auto 30px;
  text-align: center;
  color: #707070;
  font-size: 28px;
  font-weight: bold;
}

.login-form {
  border-radius: 6px;
  background: #ffffff;
  width: 450px;
  padding: 35px 40px;

  ::v-deep .el-input--large .el-input__inner {
    height: 48px;
    line-height: 48px;
  }

  ::v-deep .el-input__prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    color: #409eff;
  }

  ::v-deep .el-button--primary {
    height: 48px;
    font-size: 18px;
    letter-spacing: 4px;
  }
}

.login-field {
  width: 100%;
  height: 52px;
  display: flex;
  place-content: flex-start;
  place-items: center;
  gap: 16px;
  flex-shrink: 0;
  padding: 12px 16px;
  box-sizing: border-box;
  background: rgba(243, 245, 249, 1);

  &--flex {
    flex: 1;
    min-width: 0;
  }

  &__icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #303133;

    &::placeholder {
      color: #909399;
    }
  }
}

.login-sms-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.login-sms-btn {
  width: 144px;
  height: 52px;
  display: flex;
  place-content: center;
  place-items: center;
  gap: 4px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  border: none;
  background: rgba(15, 99, 255, 1);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.el-login-footer {
  height: 40px;
  line-height: 40px;
  position: fixed;
  bottom: 0;
  width: 100%;
  text-align: center;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 12px;
  letter-spacing: 1px;
}
</style>
